import { palette, scene } from '../../theme';

// ---------------------------------------------------------------------------
// Registry of built-in test assets. Procedural geometry, defined in code,
// always present in the asset list. Adding a new built-in is a one-file
// change — append a new entry to `builtInAssets`.
//
// Built-ins are intentionally generic (sphere, cylinder, similar). They
// exist to verify the rendering pipeline — scale, lighting, material
// behavior — not to mirror specific in-world geometry. Trunks and
// segments are the squirrel/tree designers' problem.
// ---------------------------------------------------------------------------

export interface BuiltInAsset {
  id: string;
  name: string;
  description?: string;
  /** R3F children rendered into the viewport's scene. */
  render: () => React.ReactElement;
}

export const builtInAssets: readonly BuiltInAsset[] = [
  {
    id: 'character-sphere',
    name: 'Character sphere',
    description: 'The current squirrel placeholder, in canonical orange.',
    render: () => (
      <mesh castShadow>
        <sphereGeometry args={[0.4, 32, 16]} />
        <meshStandardMaterial color={scene.characterLocal} />
      </mesh>
    ),
  },
  {
    id: 'cylinder',
    name: 'Cylinder',
    description: 'A neutral 1×2 cylinder for size and orientation checks.',
    render: () => (
      <mesh castShadow>
        <cylinderGeometry args={[0.5, 0.5, 2, 24, 1]} />
        <meshStandardMaterial color={palette.warmBeige} />
      </mesh>
    ),
  },
  {
    id: 'cube',
    name: 'Cube',
    description: 'A 1m cube — orientation reference, size sanity check.',
    render: () => (
      <mesh castShadow position={[0, 0.5, 0]}>
        <boxGeometry args={[1, 1, 1]} />
        <meshStandardMaterial color={palette.sage} />
      </mesh>
    ),
  },
];

export function findBuiltIn(id: string): BuiltInAsset | undefined {
  return builtInAssets.find(a => a.id === id);
}
